import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Send, Mic, Square, Loader2, Volume2, Check, Lightbulb } from "lucide-react";
import client from "@/lib/api";
import { useTts } from "@/hooks/useTts";
import { useLang } from "@/context/LangContext";
import { pickAudioMime, extForMime } from "@/lib/audio";

// Flag likely-nonsense transcripts (Whisper hallucinations on silence/noise,
// punctuation-only, or too short) so we can ask "Did you say this?" instead of
// blindly sending garbage. Normal speech is NOT flagged.
function looksOff(s) {
  const x = (s || "").trim().toLowerCase();
  if (x.length < 2) return true;
  if (x.replace(/[^a-zäöüßéèàçñ ]/gi, "").trim().length < 2) return true;
  const markers = ["amara", "untertitel", "subtitle", "subscribe", "thanks for watching", "thank you.", "www.", ".com"];
  return markers.some((m) => x.includes(m));
}

export default function LifeMission() {
  const { id } = useParams();
  const nav = useNavigate();
  const { play } = useTts();
  const { t } = useLang();
  const [mission, setMission] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [hints, setHints] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(false);
  const [pending, setPending] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [done, setDone] = useState(null);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null); const chunks = useRef([]);

  useEffect(() => { client.get(`/learn/life/${id}`).then(({ data }) => setMission(data)).catch(() => { toast.error(t("life_unavailable")); nav("/life"); }); }, [id, nav]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const begin = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/learn/life/start", { mission_id: id });
      setSession(data.session_id); setObjectives(data.objectives); setHints(data.hints);
      setMessages([{ role: "ai", text: data.reply }]); play(data.reply, mission.language);
    } catch (e) {} finally { setBusy(false); }
  };
  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const transcript = [...messages, { role: "user", text: msg }].map((m) => ({ role: m.role === "ai" ? "assistant" : "user", text: m.text }));
    setMessages((p) => [...p, { role: "user", text: msg }]); setInput(""); setBusy(true);
    try {
      const { data } = await client.post("/learn/life/reply", { mission_id: id, session_id: session, text: msg, transcript });
      setMessages((p) => [...p, { role: "ai", text: data.reply }]); play(data.reply, mission.language);
    } catch (e) {} finally { setBusy(false); }
  };
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); mediaRef.current = mr; chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        setBusy(true);
        try {
          const fd = new FormData(); fd.append("audio", blob, `s.${extForMime(type)}`); fd.append("target", ""); fd.append("lang", mission.language);
          const { data } = await client.post("/learn/speech-check", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setBusy(false);
          if (data.said) {
            if (looksOff(data.said)) setPending(data.said);
            else await send(data.said);
          } else { toast.error(t("common_didnt_catch")); }
        } catch (e) {
          setBusy(false);
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === "object" && detail?.error ? detail.error : t("common_didnt_catch");
          toast.error(msg);
        }
      };
      mr.start(); setRec(true);
    } catch { toast.error(t("common_mic_unavailable")); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRec(false); };
  const complete = async () => {
    setBusy(true);
    try {
      const transcript = messages.map((m) => ({ role: m.role === "ai" ? "assistant" : "user", text: m.text }));
      const { data } = await client.post("/learn/life/complete", { mission_id: id, session_id: session, transcript });
      setDone(data);
      window.dispatchEvent(new Event("mel:progress-refresh"));
    } catch { toast.error(t("common_couldnt_save")); } finally { setBusy(false); }
  };

  if (!mission) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  if (done) {
    const ok = done.completed;
    return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center px-6 text-center">
      <div className={`w-24 h-24 rounded-full flex items-center justify-center shadow-lg animate-pop ${ok ? "bg-gradient-to-br from-[#2563EB] to-[#3B82F6]" : "bg-slate-200"}`} data-testid="life-complete">
        {ok ? <Check className="text-white" size={48} /> : <span className="font-heading text-2xl font-extrabold text-slate-500">{done.objectives_met}/{done.objectives_total}</span>}
      </div>
      <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-6" data-testid="life-result-title">
        {ok ? t("life_mission_complete") : t("life_session_ended")}
      </h1>
      <p className="text-slate-600 mt-2 max-w-[320px]">{ok ? done.feedback : t("life_partial_feedback", { m: done.objectives_met, t: done.objectives_total })}</p>
      {!ok && <p className="text-xs font-bold text-amber-600 mt-2">{t("life_not_counted")}</p>}
      <div className="mt-6 w-full max-w-[340px] space-y-2 text-left">
        {(done.objectives || []).map((o, i) => (
          <div key={i} className={`flex items-center gap-2 rounded-xl px-3 py-2 border ${o.done ? "bg-white border-slate-200 text-[#0F172A]" : "bg-slate-50 border-slate-200 text-slate-400"}`} data-testid={`life-obj-${i}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${o.done ? "bg-emerald-100 text-emerald-600" : "bg-slate-200 text-slate-400"}`}>
              {o.done ? <Check size={13} /> : <span className="text-[11px] font-bold">○</span>}
            </span>
            <span className="text-sm">{o.text}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-3 w-full max-w-[340px] mt-7">
        <button onClick={() => nav("/life")} className="flex-1 border border-slate-200 bg-white text-slate-600 font-bold py-3.5 rounded-2xl" data-testid="life-back">{t("life_back")}</button>
        {!ok && <button onClick={() => { setDone(null); }} className="flex-1 bg-[#2563EB] text-white font-bold py-3.5 rounded-2xl" data-testid="life-continue-mission">{t("life_keep_going")}</button>}
      </div>
    </div>
    );
  }

  // Intro (not started)
  if (!session) return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <div className="flex items-center gap-2 px-4 pt-6 pb-2">
        <button onClick={() => nav("/life")} data-testid="life-close" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><ChevronLeft size={22} className="text-slate-600" /></button>
        <span className="text-xs font-bold text-[#2563EB] bg-[#2563EB]/10 rounded-full px-3 py-1">LIFE · {mission.level}</span>
      </div>
      <div className="px-5 pt-2">
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{mission.title}</h1>
        <p className="text-slate-600 mt-1">{mission.description}</p>
      </div>
      <div className="px-5 mt-4 flex-1">
        <img src={mission.cover} alt="" className="w-full h-56 object-cover rounded-3xl shadow-md" />
        {mission.budget && <div className="mt-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-4 py-2 text-sm font-semibold">{t("life_budget", { v: mission.budget })}</div>}
        <div className="mt-4 flex flex-wrap gap-2">
          {(mission.objectives || []).map((o, i) => <span key={i} className="text-xs text-[#2563EB] bg-[#2563EB]/10 rounded-full px-3 py-1.5 font-semibold">{o}</span>)}
        </div>
      </div>
      <div className="px-5 pb-8">
        <button onClick={begin} disabled={busy} data-testid="start-mission-btn" className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg">
          {busy ? <Loader2 className="animate-spin" size={18} /> : t("life_start_mission")}
        </button>
      </div>
    </div>
  );

  // Active conversation (light, high-contrast)
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <div className="bg-white border-b border-slate-200 px-4 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => nav("/life")} data-testid="life-close" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><ChevronLeft size={20} className="text-slate-600" /></button>
        <div className="flex-1 min-w-0"><p className="font-bold text-[#0F172A] truncate">{mission.title}</p><p className="text-slate-400 text-xs truncate">{mission.subtitle}</p></div>
      </div>

      <div className="px-4 pt-3">
        <img src={mission.cover} alt="" className="w-full h-40 object-cover rounded-2xl" />
        <div className="mt-2 flex gap-2 flex-wrap">{objectives.map((o, i) => <span key={i} className="text-[11px] text-[#2563EB] bg-[#2563EB]/10 rounded-full px-2.5 py-1 font-semibold">{o}</span>)}</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="life-transcript">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] rounded-2xl px-4 py-2.5 shadow-sm ${m.role === "user" ? "bg-[#2563EB] text-white" : "bg-white border border-slate-200 text-[#0F172A]"}`}>
              <p className="text-sm leading-relaxed">{m.text}</p>
              {m.role === "ai" && <button onClick={() => play(m.text, mission.language)} className="mt-1 text-[#2563EB] inline-flex items-center gap-1 text-xs font-semibold"><Volume2 size={13} /> {t("common_listen")}</button>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-4 py-3"><Loader2 className="animate-spin text-[#2563EB]" size={16} /></div></div>}
      </div>

      {showHint && <div className="px-4 pb-2 flex flex-wrap gap-2">{hints.map((h, i) => <button key={i} onClick={() => send(h)} className="text-xs bg-amber-100 text-amber-800 font-semibold rounded-full px-3 py-1.5" data-testid={`hint-${i}`}>{h}</button>)}</div>}

      {pending && (
        <div className="px-4 pb-2" data-testid="voice-confirm">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
            <p className="text-xs font-semibold text-amber-800 mb-1">{t("voice_confirm_title")}</p>
            <p className="text-sm text-[#0F172A] mb-2 italic">"{pending}"</p>
            <div className="flex gap-2">
              <button onClick={() => { const v = pending; setPending(null); send(v); }} data-testid="voice-confirm-send" className="flex-1 bg-[#2563EB] text-white text-sm font-bold rounded-xl py-2">{t("voice_confirm_send")}</button>
              <button onClick={() => { setPending(null); startRec(); }} data-testid="voice-confirm-retry" className="flex-1 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl py-2">{t("voice_confirm_retry")}</button>
            </div>
          </div>
        </div>
      )}

      <div className="px-3 pb-4 pt-2 bg-white border-t border-slate-200" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 12px)" }}>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHint((v) => !v)} data-testid="hint-toggle" className="w-11 h-11 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center shrink-0"><Lightbulb size={18} /></button>
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="life-input"
            placeholder={t("life_input_ph")} className="flex-1 rounded-full bg-slate-100 px-4 py-3 outline-none text-[#0F172A] focus:ring-2 focus:ring-[#2563EB]/40" />
          {input.trim() ? (
            <button onClick={() => send()} disabled={busy} data-testid="life-send" className="w-11 h-11 rounded-full bg-[#2563EB] text-white flex items-center justify-center shrink-0"><Send size={18} /></button>
          ) : (
            <button onClick={rec ? stopRec : startRec} disabled={busy} data-testid="life-mic" className={`w-11 h-11 rounded-full text-white flex items-center justify-center shrink-0 ${rec ? "bg-rose-500 animate-pulse" : "bg-[#2563EB]"}`}>{rec ? <Square size={16} /> : <Mic size={18} />}</button>
          )}
        </div>
        <button onClick={complete} disabled={busy} data-testid="life-finish" className="w-full mt-2 text-[#475569] text-sm font-semibold py-2">{t("life_end_session")}</button>
      </div>
    </div>
  );
}
